import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/contexts/ToastContext'
import { loginUsuario } from '@/api/usuariosApi'
import { markClienteSessionActive } from '@/utils/clienteAuth'
import { CONTEUDO_CHAVES, getConteudoPublic } from '@/api/adminApi'
import { consumeAuthNotice } from '@/utils/authFlow'
import { getClientPublicEntryPath } from '@/config/tenant'
import { subscribeConteudoUpdated } from '@/utils/conteudoSync'
import { useTenant } from '@/contexts/TenantContext'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import styles from './LoginPage.module.css'

export function LoginPage() {
  const navigate = useNavigate()
  const { nome: empresaNome, logoUrl: tenantLogoUrl, slug: tenantSlug } = useTenant()
  const { user, setUser } = useUser()
  const { toast } = useToast()
  const [email, setEmail] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState('')
  const [shake, setShake] = useState(false)
  const [loading, setLoading] = useState(false)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)

  useEffect(() => {
    const msg = consumeAuthNotice('cliente')
    if (msg) {
      setErro(msg)
      toast(msg, 'info')
    }
  }, [toast])

  const loadLoginContent = useCallback(() => {
    return getConteudoPublic()
      .then((data) => {
        const logo = data[CONTEUDO_CHAVES.app_logo_url] ?? null
        setLogoUrl(logo ?? tenantLogoUrl)
      })
      .catch(() => {
        setLogoUrl(tenantLogoUrl)
      })
  }, [tenantLogoUrl])

  useEffect(() => {
    let cancelled = false
    const run = () => {
      if (cancelled) return
      void loadLoginContent()
    }
    run()
    const unsub = subscribeConteudoUpdated(run)
    return () => {
      cancelled = true
      unsub()
    }
  }, [tenantSlug, loadLoginContent])

  if (user) return <Navigate to="/menu" replace />

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErro('')

    const emailTrim = email.trim()
    const codigoTrim = codigo.trim()

    if (!emailTrim) {
      const msg = 'Digite o seu e-mail.'
      setErro(msg)
      toast(msg, 'error')
      setShake(true)
      setTimeout(() => setShake(false), 500)
      return
    }
    if (!codigoTrim) {
      const msg = 'Digite o código de acesso.'
      setErro(msg)
      toast(msg, 'error')
      setShake(true)
      setTimeout(() => setShake(false), 500)
      return
    }
    if (codigoTrim.length < 4) {
      const msg = 'O código deve ter pelo menos 4 caracteres.'
      setErro(msg)
      toast(msg, 'error')
      setShake(true)
      setTimeout(() => setShake(false), 500)
      return
    }

    setLoading(true)
    try {
      const result = await loginUsuario(emailTrim, codigoTrim)
      markClienteSessionActive()
      setUser({
        id: result.id,
        nome: result.nome,
        telefone: result.telefone,
        email: result.email,
        avatar_url: result.avatar_url ?? null,
      })
      toast(`Bem-vindo de volta, ${result.nome}!`, 'success')
      navigate('/menu', { replace: true })
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro ao entrar.')
      setShake(true)
      setTimeout(() => setShake(false), 500)
      toast(err instanceof Error ? err.message : 'Erro ao entrar.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const displayLogo = resolveMediaUrl(logoUrl || tenantLogoUrl || '')

  return (
    <main className={`standalone-page ${styles.page} animate-slide-up`}>
      <div className={styles.logoBlock}>
        <div className={styles.logo}>
          {displayLogo ? (
            <img src={displayLogo} alt="Logo" className={styles.logoImg} />
          ) : (
            'LOGO'
          )}
        </div>
        <span className={styles.appName}>{empresaNome}</span>
      </div>

      <div className={`${styles.card} ${shake ? 'animate-shake' : ''}`}>
        <button
          type="button"
          className={styles.textLinkBtn}
          onClick={() => navigate(getClientPublicEntryPath())}
          style={{ marginBottom: 14, display: 'inline-flex' }}
        >
          ← Voltar
        </button>
        <span className={styles.kicker}>Área do cliente</span>
        <h1 className={styles.heading}>Entrar</h1>
        <p className={styles.subheading}>
          Utilize o <strong>mesmo e-mail</strong> e o <strong>mesmo código de acesso</strong> que criou no registo de
          cliente. Se ainda não se registou, use a opção abaixo.
        </p>

        {erro && <p className={styles.erro} role="alert">{erro}</p>}

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="email">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              className={styles.input}
              placeholder="Digite o seu e-mail"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setErro('')
              }}
              autoComplete="username"
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="codigo">
              Código de acesso
            </label>
            <input
              id="codigo"
              type="password"
              className={styles.input}
              placeholder="Digite o código de acesso"
              value={codigo}
              onChange={(e) => {
                setCodigo(e.target.value)
                setErro('')
              }}
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className={styles.btnEntrar} disabled={loading}>
            {loading ? 'A entrar...' : 'Entrar'}
          </button>
        </form>
        <p className={styles.cadastroHint}>
          <button type="button" className={styles.textLinkBtn} onClick={() => navigate('/cadastro')}>
            Primeira vez aqui? Criar conta de cliente
          </button>
          {' · '}
          <button type="button" className={styles.textLinkBtn} onClick={() => navigate('/recuperar-codigo')}>
            Esqueci o código de acesso
          </button>
        </p>
      </div>

    </main>
  )
}
